import { apiClient } from './api';
import type { 
  Organization, 
  CreateOrganizationData,
  UpdateOrganizationData,
  PaginatedOrganizationResponse 
} from '../types/organization';

export class OrganizationService {
  async getOrganizations(params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    isActive?: boolean;
  }): Promise<PaginatedOrganizationResponse> {
    const result = await apiClient.getOrganizations(params);
    
    // Handle pagination info from backend
    const pagination = result.pagination;
    return {
      organizations: result.organizations as unknown as Organization[],
      total: pagination?.total || result.organizations.length,
      page: pagination?.page || params?.page || 1,
      limit: pagination?.limit || params?.limit || 25,
      totalPages: pagination?.totalPages || Math.ceil((pagination?.total || result.organizations.length) / (pagination?.limit || params?.limit || 25))
    };
  }

  async getOrganizationById(id: string): Promise<Organization> {
    const result = await apiClient.getOrganizationById(id);
    return result.organization as unknown as Organization;
  }

  async createOrganization(data: CreateOrganizationData): Promise<Organization> {
    const result = await apiClient.createOrganization(data as unknown as CreateOrganizationData);
    return result.organization as unknown as Organization;
  }

  async updateOrganization(id: string, data: UpdateOrganizationData): Promise<Organization> {
    const result = await apiClient.updateOrganization(id, data as unknown as UpdateOrganizationData);
    return result.organization as unknown as Organization;
  }

  async deleteOrganization(id: string): Promise<void> {
    await apiClient.deleteOrganization(id);
  }

  async uploadLogo(id: string, file: File): Promise<Organization> {
    const result = await apiClient.uploadOrganizationLogo(id, file);
    return result.organization as unknown as Organization;
  }

  async getMyUbuntuOrganization(): Promise<Organization> {
    // Get MyUbuntu organization by searching for code 'default'
    const response = await this.getOrganizations({ search: 'default', limit: 10 });
    
    // Find the organization with code 'default' (which is MyUbuntu)
    const myubuntu = response.organizations.find(org => 
      org.code === 'default' || org.name.toLowerCase().includes('myubuntu')
    );
    
    if (!myubuntu) {
      throw new Error('MyUbuntu organization not found in database');
    }
    
    return myubuntu;
  }

  async getOrganizationsForSelection(params?: {
    search?: string;
    type?: string;
  }): Promise<{ organizations: Organization[] }> {
    const result = await apiClient.getOrganizationsForSelection(params);
    return {
      organizations: result.organizations as unknown as Organization[]
    };
  }
}

export const organizationService = new OrganizationService(); 